import {BaseTransaction} from "../../../../../api/baseTransactions/BaseTransaction.ts";
import React, {useEffect, useState} from "react";
import {BackendApi} from "../../../../../api/api.ts";
import {BaseTransactionsRepository} from "../../../../../api/baseTransactions/BaseTransactionsRepository.ts";
import Stats, { OutstandingBillSummary } from "../../Stats.tsx";
import MonthlyInflowOutflowGraph from "../CashflowPage/MonthlyInflowOutflowGraph.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {Transaction} from "../../TransactionsTable.tsx";
import {DashboardRecentTransactionsTable} from "./DashboardRecentTransactionsTable.tsx";
import {CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {CustomerBankAccountRepository} from "../../../../../api/customerBankAccounts/CustomerBankAccountRepository.ts";
import {Link} from "react-router-dom";
import RevenueCategoriesPieChart from "./RevenueCategoriesPieChart.tsx";
import ExpenseCategoriesPieChart from "./ExpenseCategoriesPieChart.tsx";
import {CashflowPageState, deserializeCashflowPageState} from "../CashflowPage/CashflowPageState.ts";
import {useNavigate} from "react-router-dom";
import {canUseInvoicing} from "../../../../../lib/invoicingAccess.ts";

type DashboardCategoryBreakdown = {
    expenses: Record<string, {
        percentage: number;
        amount: number;
    }>;
    revenues: Record<string, {
        percentage: number;
        amount: number;
    }>;
};

export default function DashboardEntrepreneur({currentUser}: { currentUser: User }) {
    const navigate = useNavigate();
    const greetingEmojis = ['✨', '👋', '🙌', '🚀']
    const selectedGreetingEmoji = greetingEmojis[Math.floor(Math.random() * greetingEmojis.length)];

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [transactions, setTransactions] = useState<Array<Transaction>>([]);
    const [bankAccounts, setBankAccounts] = useState<Array<CustomerBankAccount>>([]);
    const [boiGraph, setBoiGraph] = useState();
    const [baseStats, setBaseStats] = useState();
    const [outstandingBillSummary, setOutstandingBillSummary] = useState<OutstandingBillSummary | null>(null);
    const [allocations, setAllocations] = useState<DashboardCategoryBreakdown>();

    useEffect(() => {
        onLoad()
    }, []);

    const api = new BackendApi(currentUser.getToken())

    async function onLoad() {
        try {
            const transactionRepository = new BaseTransactionsRepository(api);
            const data = await transactionRepository.getBusinessBankTransactions(currentUser.businesses[0], 0, 16);
            setTransactions(
                data.results.map((original: BaseTransaction) => ({
                    id: original.id,
                    desc: original.desc,
                    generalLedgerAccount: original.generalLedgerAccount,
                    bookingDate: original.date,
                    amount: original.amount,
                    flow: original.flow,
                    relationName: original.relationName,
                    relationIBAN: original.iban,
                    value: original.value,
                    accountId: original.accountId,
                })),
            );

            const [baseStatsData, boiGraphData, outstandingBillsData, categoryData] = await Promise.all([
                api.Business__stats__baseStats({}),
                api.Business__stats__iobGraphData(null,null,null),
                api.Entrepreneur__outstandingBills({}),
                api.Business__stats__getMonthlyTransactionCategoryBreakdown(null),
            ]);

            setBaseStats(baseStatsData);
            setBoiGraph(boiGraphData);
            setOutstandingBillSummary(outstandingBillsData.summary);
            setAllocations(categoryData as DashboardCategoryBreakdown);

            const customerBankAccountRepository = new CustomerBankAccountRepository(api);
            setBankAccounts(await customerBankAccountRepository.getMyBankAccounts());
        } catch (error) {
            console.error(error);
            setOutstandingBillSummary(null);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <Shell currentUser={currentUser} isLoading={isLoading} title={`Hey ${currentUser.firstName} ${selectedGreetingEmoji}`}>
                <Stats data={baseStats} outstandingBillSummary={outstandingBillSummary} showOutstandingInvoices={canUseInvoicing(currentUser.businesses)}/>
                <div className="flex flex-col md:flex-row gap-4 mt-4">
                    <RevenueCategoriesPieChart data={allocations} />
                    <ExpenseCategoriesPieChart data={allocations} />
                </div>
                <div className="grid xl:grid-cols-3 grid-cols-1 auto-rows-max gap-4 mt-8">
                    <div className="col-span-3 relative">
                        <div className="flex justify-between items-center pt-6">
                            <h3 className="text-xl font-gray-900 py-3">Cashflow</h3>
                            <Link to="/cash-flow" className="text-blue-400 hover:text-blue-500 text-sm uppercase">
                                Lees meer →
                            </Link>
                        </div>
                        <MonthlyInflowOutflowGraph data={boiGraph} className={"h-[30vh]"}
                                                   onBarClick={(state: CashflowPageState) => {
                                                       navigate('/cash-flow?' + deserializeCashflowPageState(state))
                                                   }}/>
                    </div>
                </div>

                <div className="w-full mt-8">
                    <h3 className="text-xl font-gray-900 py-3 pt-6">Recente transacties</h3>

                    <div className="w-full">
                        {transactions && bankAccounts.length &&
                            <DashboardRecentTransactionsTable
                                transactions={transactions}
                                bankAccounts={bankAccounts}
                            />}
                    </div>
                </div>
            </Shell>
        </>
    )
}

